import { useMemo } from 'react';
import { Case, Else, If, Switch, Then, When } from 'react-if';
import cns from 'classnames';
import { useTranslation } from 'next-i18next';
import { CheckIcon, ExportIcon, PlusIcon } from '@zep/icons';
import { Button, Radio } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { SubscribePlan } from '../../../../../types/SubscriptionType';
import { fCurrency } from '../../../../../utils';
import { BASIC_NEW_FEATURES, ENTERPRISE_NEW_FEATURES, PRO_NEW_FEATURES } from '../../SubscriptionConst';
import { capitalizeFirstLetter, usePremiumOptionName } from '../../subscriptionUtil';
import { PlanCheckoutSection } from '../atoms/PlanCheckoutSection';
import { useSubscriptionPaymentContext } from '../hooks';
import S from './SpacePlanSelectButtons.module.scss';
export const SpacePlanSelectButtons = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spacePlanProducts,
    showEnterpriseSample
  } = useSubscriptionPaymentContext();
  const {
    FREE: _,
    ...rest
  } = spacePlanProducts;
  // 정렬 순서 이슈로인해서 각 키의 첫번째 값들을 id 로 크기를 비교해서 플랜값들을 만듭니다
  const plansKeys = Object.values(rest).map(d => !!d.length && d[0]).sort((a, b) =>
  // @ts-ignore
  a?.id > b?.id ? 1 : -1).map(d =>
  // @ts-ignore
  (d?.name || '').toLowerCase()) as SubscribePlan[];
  return <div className={S.wrapper} data-sentry-component="SpacePlanSelectButtons" data-sentry-source-file="SpacePlanSelectButtons.tsx">
      <PlanCheckoutSection.Title data-sentry-element="unknown" data-sentry-source-file="SpacePlanSelectButtons.tsx">
        {t('subscription:payment.planSelect.subtitlePlanType')}
      </PlanCheckoutSection.Title>
      <div className={S.button_wrapper}>
        {plansKeys.map(plan => <PlanRadioButton plan={plan} key={plan} />)}
        <When condition={showEnterpriseSample} data-sentry-element="When" data-sentry-source-file="SpacePlanSelectButtons.tsx">
          <EnterPriseSample data-sentry-element="EnterPriseSample" data-sentry-source-file="SpacePlanSelectButtons.tsx" />
        </When>
      </div>
    </div>;
};
const PlanRadioButton = ({
  plan
}: {
  plan: Omit<SubscribePlan, 'free'>;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    handleSelectedPlan: _handleSubscribePlan,
    selectedPlan,
    spacePlanProducts,
    selectedMaxCcu,
    currency,
    subscriptionPeriod
  } = useSubscriptionPaymentContext();
  const isDisabledPlanMaxCcu = (() => {
    const key = plan === 'pro' ? 'PRO' : plan === 'basic' ? 'BASIC' : 'ENTERPRISE';
    // 선택한 maxCcu 가 존재하지 않으면 선택이 불가능한 케이스 입니다
    return !spacePlanProducts[key].some(d => d.maxCcu === selectedMaxCcu);
  })();
  const calcPersonPrice = useMemo(() => (type: Omit<SubscribePlan, 'free'>) => {
    const key = type === 'pro' ? 'PRO' : type === 'basic' ? 'BASIC' : 'ENTERPRISE';
    const products = spacePlanProducts[key].filter(d => d.maxCcu === selectedMaxCcu).filter(p => p.subscriptionPeriod === subscriptionPeriod);
    if (products.length === 0) return;
    const price = Number((products[0].salePrice / Number(selectedMaxCcu)).toFixed(currency === 'KRW' ? 0 : 2));
    return fCurrency(subscriptionPeriod === 365 ? +(price / 12).toFixed(2) : price, currency, {
      currencyString: t('subscription:currency.won')
    });
  }, [currency, selectedMaxCcu, spacePlanProducts, subscriptionPeriod, t]);
  const handleSubscribePlan = () => {
    if (plan === 'basic' && isDisabledPlanMaxCcu) return;
    _handleSubscribePlan(plan);
  };
  return <div className={cns(S.container, {
    [S.container_active]: plan === selectedPlan
  })} onClick={handleSubscribePlan} data-sentry-component="PlanRadioButton" data-sentry-source-file="SpacePlanSelectButtons.tsx">
      <div className={S.radio_wrapper}>
        <Radio disabled={isDisabledPlanMaxCcu && Object.keys(spacePlanProducts).length !== 1} label={<div style={{
        display: 'flex',
        gap: 4
      }}>
              {capitalizeFirstLetter(plan as string)}
              <When condition={plan === 'pro'}>
                <Recommend />
              </When>
            </div>} checked={plan === selectedPlan} onChange={handleSubscribePlan} labelPlacement={'left'} data-sentry-element="Radio" data-sentry-source-file="SpacePlanSelectButtons.tsx" />
      </div>
      <div className={S.desc_wrapper}>
        <If condition={isDisabledPlanMaxCcu} data-sentry-element="If" data-sentry-source-file="SpacePlanSelectButtons.tsx">
          <Then data-sentry-element="Then" data-sentry-source-file="SpacePlanSelectButtons.tsx">
            <span className={S.title}>
              {t('subscription:payment.planSelect.basicPlanException', {
              ccu: '30'
            })}
            </span>
          </Then>
          <Else data-sentry-element="Else" data-sentry-source-file="SpacePlanSelectButtons.tsx">
            <div className={S.title} style={{
            display: 'flex',
            gap: 2,
            alignItems: 'center'
          }}>
              {selectedMaxCcu < 0 ? t('subscription:pricing.calculation.barUnlimited') : t('subscription:payment.planSelect.maxCcu', {
              ccu: selectedMaxCcu?.toString()
            })}
              <When condition={selectedMaxCcu >= 0} data-sentry-element="When" data-sentry-source-file="SpacePlanSelectButtons.tsx">
                <div className={S.sub_box}>
                  {t('subscription:payment.planSelect.basicPricePerPeople', {
                  price: calcPersonPrice(plan)
                })}{' '}
                  / {t('subscription:payment.summaryCommon.monthly')}
                </div>
              </When>
            </div>
          </Else>
        </If>
        <PlanFeaturePayment plan={plan} data-sentry-element="PlanFeaturePayment" data-sentry-source-file="SpacePlanSelectButtons.tsx" />
      </div>
    </div>;
};
const PlanFeaturePayment = ({
  plan
}: {
  plan: Omit<SubscribePlan, 'free'>;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spacePlanProducts
  } = useSubscriptionPaymentContext();
  const optionKeys = Object.keys(spacePlanProducts?.ENTERPRISE?.[0]?.option ?? {}).filter(key =>
  // @ts-ignore
  spacePlanProducts?.ENTERPRISE[0]?.option[key]).filter(key => key !== 'additionalInformation');
  const optionNames = usePremiumOptionName();
  return <Switch data-sentry-element="Switch" data-sentry-component="PlanFeaturePayment" data-sentry-source-file="SpacePlanSelectButtons.tsx">
      <Case condition={plan === 'basic'} data-sentry-element="Case" data-sentry-source-file="SpacePlanSelectButtons.tsx">
        {BASIC_NEW_FEATURES.map(desc => <span className={S.desc} key={desc.key}>
            {desc.icon({
          width: 14,
          height: 14,
          fill: Colors.PrimaryDefault
        })}
            {t(desc.key)}
          </span>)}
      </Case>
      <Case condition={plan === 'pro'} data-sentry-element="Case" data-sentry-source-file="SpacePlanSelectButtons.tsx">
        <span className={S.title}>
          {t('subscription:payment.planSelect.featureBasicAll')}
        </span>
        {PRO_NEW_FEATURES.map(desc => <span className={S.desc} key={desc.key}>
            {desc.icon({
          width: 14,
          height: 14,
          fill: Colors.PrimaryDefault
        })}
            {t(desc.key)}
          </span>)}
      </Case>
      <Case condition={plan === 'enterprise'} data-sentry-element="Case" data-sentry-source-file="SpacePlanSelectButtons.tsx">
        {optionKeys.map(key => <span className={S.desc} key={key}>
            <CheckIcon width={14} height={14} fill={Colors.PrimaryDefault} />
            {optionNames[key as keyof typeof optionNames]}
          </span>)}
      </Case>
    </Switch>;
};
const EnterPriseSample = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <div className={S.container_enterprise} data-sentry-component="EnterPriseSample" data-sentry-source-file="SpacePlanSelectButtons.tsx">
      <div className={S.radio_wrapper}>
        <div className={S.label}>
          <PlusIcon width={18} height={18} data-sentry-element="PlusIcon" data-sentry-source-file="SpacePlanSelectButtons.tsx" />
          <div className="ml-[8px]">Enterprise</div>
          <Inquiry data-sentry-element="Inquiry" data-sentry-source-file="SpacePlanSelectButtons.tsx" />
        </div>
      </div>
      <div className={S.desc_wrapper}>
        <div className={S.row}>
          <div className={S.col}>
            <span className={S.title}>
              {t('subscription:payment.planSelect.featureProAll')}
            </span>
            {ENTERPRISE_NEW_FEATURES.map(desc => <span className={S.desc} key={desc.key}>
                {desc.icon({
              width: 14,
              height: 14,
              fill: Colors.PrimaryDefault
            })}
                height={14}
                fill={Colors.PrimaryDefault}
                {t(desc.key)}
              </span>)}
            <span className={S.desc} style={{
            fontWeight: 600
          }}>
              {t('subscription:pricing.features.enterpriseSelect')}
            </span>
          </div>
          <div className={S.col}>
            <Button leftSlot={<ExportIcon width={14} height={14} color={Colors.PrimaryDefault} />} as={'a'} href={t('subscription:pricing.plansEnterprise.ctaLink')} target={'_blank'} variant={'secondary'} size={'xs'} data-sentry-element="Button" data-sentry-source-file="SpacePlanSelectButtons.tsx">
              {t('subscription:pricing.plansEnterprise.buttonContact')}
            </Button>
          </div>
        </div>
      </div>
    </div>;
};
const Recommend = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <div className={S.recommend} data-sentry-component="Recommend" data-sentry-source-file="SpacePlanSelectButtons.tsx">
      {t('subscription:payment.planSelect.proTag')}
    </div>;
};
const Inquiry = () => {
  const {
    t
  } = useTranslation(['subscription']);
  return <div className={S.inquiry} data-sentry-component="Inquiry" data-sentry-source-file="SpacePlanSelectButtons.tsx">
      {t('subscription:payment.planSelect.enterpriseTag')}
    </div>;
};